"use client";

import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { useForm } from "react-hook-form";
import { useKmsService } from "../../providers/kms-service-provider";
import { translateApiError } from "../../utils/translate-api-error";
import {
  EMAIL_REGEX,
  PHONE_REGEX,
  REQUIRED,
} from "../../constants/regex-const";
import { Contact } from "../../types/models/contact";
import { Collaborateur } from "../../types/models/collaborateur";
import { ContactForm, PickedContact } from "../../types/forms/contact-form";
import { Field } from "./field";
import Icon from "../shared/ui/icon";
import { titleCase } from "../../utils/titleCase";

type ModalView = "list" | "create";

const EMPTY_FORM: ContactForm = {
  prenom: "",
  nom: "",
  email: "",
  phone: "",
};

function displayName(picked: PickedContact): string {
  const { nom, prenom } =
    picked.kind === "existing" ? picked.contact : picked.info;
  return `${titleCase(prenom)} ${titleCase(nom)}`;
}

function displayEmail(picked: PickedContact): string {
  return picked.kind === "existing" ? picked.contact.email : picked.info.email;
}

// si on reçoit un collaborateur, on le transforme en contact
function collaborateurToContact(collaborateur: Collaborateur): Contact {
  return {
    id: collaborateur.idContact,
    nom: collaborateur.nom,
    prenom: collaborateur.prenom,
    email: collaborateur.email,
    telephone: collaborateur.telephone,
    isClient: true,
    isChauffeur: false,
    isAdministrateur: false,
  };
}

export default function ContactPickerModal({
  contacts: providedContacts,
  collaborateurs: providedCollaborateurs,
  selected: controlledSelection = null,
  onSelect,
  readonly = false,
  hideList = false,
  hideCreate = false,
}: {
  // Liste de contacts déjà chargée par l'appelant (p. ex. collaborateurs
  // non-gestionnaires + carnet d'adresse d'une entreprise) : quand elle est
  // fournie, elle remplace le chargement de tous les contacts ci-dessous.
  contacts?: Contact[];
  // Alternative à `contacts` quand l'appelant n'a que des Collaborateur sous
  // la main (p. ex. GET /collaborateurs/entreprise/{id}) : évite à chaque
  // appelant de refaire la conversion idContact → Contact lui-même. Ignoré
  // si `contacts` est fourni.
  collaborateurs?: Collaborateur[];
  selected?: PickedContact | null;
  onSelect: (picked: PickedContact | null) => void;
  readonly?: boolean;
  // Saute entièrement la liste (et son chargement) : la modale s'ouvre
  // directement sur le formulaire de création, sans retour possible vers une
  // liste — utilisé quand l'appelant n'a aucun carnet d'adresse pertinent à
  // proposer (p. ex. un client non rattaché à une entreprise).
  hideList?: boolean;
  // Masque le bouton de création : la liste fournie est alors la seule source
  // possible (p. ex. les administrateurs d'une entreprise).
  hideCreate?: boolean;
}) {
  const t = useTranslations("contact_picker");
  const { contactService } = useKmsService();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ModalView>(hideList ? "create" : "list");

  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [previewId, setPreviewId] = useState<number | null>(
    controlledSelection?.kind === "existing"
      ? controlledSelection.contact.id
      : null,
  );

  // Recharge les contacts à chaque ouverture de la modale
  useEffect(() => {
    if (!open) return;
    setView(hideList ? "create" : "list");
    if (hideList) return;
    setLoadError(null);
    setPreviewId(
      controlledSelection?.kind === "existing"
        ? controlledSelection.contact.id
        : null,
    );
    const preloaded =
      providedContacts ?? providedCollaborateurs?.map(collaborateurToContact);
    if (preloaded) {
      setContacts(preloaded);
      setPreviewId((current) => current ?? preloaded[0]?.id ?? null);
      return;
    }
    contactService
      .getAllContacts()
      .then((data: Contact[]) => {
        setContacts(data);
        setPreviewId((current) => current ?? data[0]?.id ?? null);
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des contacts:", error);
        setLoadError(translateApiError(error));
      });
  }, [
    open,
    hideList,
    contactService,
    providedContacts,
    providedCollaborateurs,
  ]);

  const filteredContacts = useMemo(() => {
    if (!contacts) return [];
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      `${c.prenom} ${c.nom} ${c.email}`.toLowerCase().includes(q),
    );
  }, [contacts, query]);

  const previewedContact = useMemo(
    () => contacts?.find((c) => c.id === previewId) ?? null,
    [contacts, previewId],
  );

  function closeModal() {
    setOpen(false);
    setView("list");
    setQuery("");
  }

  function handleConfirmSelection(contact: Contact) {
    onSelect({ kind: "existing", contact });
    closeModal();
  }

  function handleRemoveSelection(e: React.MouseEvent) {
    e.stopPropagation();
    onSelect(null);
  }

  // Le contact "créé à la volée" n'est pas envoyé à l'API ici : ses infos
  // sont juste sélectionnées, et c'est la création de la course qui les
  // enverra en inline (demandeur/passager) — voir CreateContactDto.
  function handleContactCreated(data: ContactForm) {
    onSelect({ kind: "new", info: data });
    closeModal();
  }

  if (readonly) {
    return (
      <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 dark:border-white bg-slate-50/50 dark:bg-slate-50/10">
        <Icon
          name="contact_mail"
          className="text-lg text-slate-400 dark:text-white"
        />
        {controlledSelection ? (
          <span className="flex-1 text-left text-sm font-medium text-slate-800 dark:text-white truncate">
            {displayName(controlledSelection)} —{" "}
            {displayEmail(controlledSelection)}
          </span>
        ) : (
          <span className="flex-1 text-left text-sm text-slate-400 dark:text-white">
            {t("no_contact_selected")}
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 dark:border-white bg-slate-50/50 dark:bg-slate-50/10 hover:border-primary/40 hover:bg-primary/5 dark:hover:bg-primary/5 transition-all"
      >
        <Icon
          name="contact_mail"
          className="text-lg text-slate-400 dark:text-white"
        />
        {controlledSelection ? (
          <span className="flex-1 text-left text-sm font-medium text-slate-800 dark:text-white truncate">
            {displayName(controlledSelection)} —{" "}
            {displayEmail(controlledSelection)}
          </span>
        ) : (
          <span className="flex-1 text-left text-sm text-slate-400 dark:text-white">
            {t("placeholder")}
          </span>
        )}
        {controlledSelection && (
          <span
            role="button"
            onClick={handleRemoveSelection}
            className="p-1 text-red-500 hover:bg-red-500/10 rounded-full transition-colors"
          >
            <Icon name="close" className="text-lg" />
          </span>
        )}
      </button>

      <Transition show={open} as={Fragment}>
        <Dialog onClose={closeModal} className="relative z-1000 dark">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <DialogPanel className="w-full max-w-4xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl overflow-hidden">
                {view === "list" ? (
                  <ContactListView
                    query={query}
                    onQueryChange={setQuery}
                    contacts={filteredContacts}
                    isLoading={contacts === null && !loadError}
                    loadError={loadError}
                    previewedContact={previewedContact}
                    onPreview={setPreviewId}
                    onConfirm={handleConfirmSelection}
                    onCreateNew={
                      hideCreate ? undefined : () => setView("create")
                    }
                  />
                ) : (
                  <CreateContactForm
                    onCreated={handleContactCreated}
                    onCancel={hideList ? closeModal : () => setView("list")}
                    showBackLink={!hideList}
                  />
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

// ---------------------------------------------------------------------------
// List + preview view
// ---------------------------------------------------------------------------

function ContactListView({
  query,
  onQueryChange,
  contacts,
  isLoading,
  loadError,
  previewedContact,
  onPreview,
  onConfirm,
  onCreateNew,
}: {
  query: string;
  onQueryChange: (v: string) => void;
  contacts: Contact[];
  isLoading: boolean;
  loadError: string | null;
  previewedContact: Contact | null;
  onPreview: (id: number) => void;
  onConfirm: (contact: Contact) => void;
  onCreateNew?: () => void;
}) {
  const t = useTranslations("contact_picker");

  return (
    <div className="flex flex-col sm:flex-row h-[min(80dvh,600px)]">
      <div className="flex min-w-0 flex-1 flex-col border-b border-slate-200 dark:border-slate-700 sm:border-b-0 sm:border-r">
        {/* Barre de recherche */}
        <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-700">
          <Field
            titre=""
            value={query}
            icon="search"
            placeholder={t("search_placeholder")}
            onChange={(e) => onQueryChange(e.target.value)}
          />
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-2">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-center animate-pulse">
              <div className="h-12 w-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                <Icon
                  name="progress_activity"
                  className="text-slate-400 dark:text-slate-500 text-xl animate-spin"
                />
              </div>
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                {t("loading")}
              </p>
            </div>
          )}

          {!isLoading && loadError && (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-50 dark:bg-red-950/30 mb-3">
                <Icon name="error" className="text-2xl text-red-400" />
              </div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {loadError}
              </p>
            </div>
          )}

          {!isLoading && !loadError && contacts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 mb-3">
                <Icon name="group_off" className="text-2xl text-slate-400" />
              </div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {t("empty")}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t("empty_hint")}
              </p>
            </div>
          )}

          {!isLoading &&
            !loadError &&
            contacts.map((c) => {
              const isSelected = previewedContact?.id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onMouseEnter={() => onPreview(c.id)}
                  onClick={() => onConfirm(c)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                >
                  <span className="flex size-9 flex-none items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {titleCase(c.prenom)[0]}
                    {titleCase(c.nom)[0]}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-slate-800 dark:text-white">
                      {titleCase(c.prenom)} {titleCase(c.nom)}
                    </span>
                    <span className="block truncate text-xs text-slate-400 dark:text-slate-500">
                      {c.email}
                    </span>
                  </span>
                  <Icon
                    name="chevron_right"
                    className="text-slate-300 dark:text-slate-600 text-sm flex-none"
                  />
                </button>
              );
            })}
        </div>

        {/* Créer un nouveau contact */}
        {onCreateNew && (
          <div className="p-4 sm:p-5 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onCreateNew}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white shadow-md shadow-black/20 hover:bg-primary/90 transition-all"
            >
              <Icon name="person_add" className="text-sm" />
              {t("create_new")}
            </button>
          </div>
        )}
      </div>

      {/* Aperçu */}
      {previewedContact ? (
        <div className="hidden w-96 flex-none flex-col divide-y divide-slate-100 overflow-y-auto sm:flex dark:divide-slate-700">
          <div className="flex-none p-6 text-center">
            <span className="mx-auto flex size-16 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
              {titleCase(previewedContact.prenom)[0]}
              {titleCase(previewedContact.nom)[0]}
            </span>
            <h2 className="mt-3 font-semibold text-slate-800 dark:text-white">
              {titleCase(previewedContact.prenom)} {titleCase(previewedContact.nom)}
            </h2>
          </div>
          <div className="flex flex-auto flex-col justify-between p-6">
            <dl className="grid grid-cols-1 gap-y-3 text-sm text-slate-600 dark:text-slate-300">
              {previewedContact.telephone && (
                <div className="flex items-center gap-2">
                  <Icon name="phone" className="text-slate-400 text-sm" />
                  <dd>{previewedContact.telephone}</dd>
                </div>
              )}
              <div className="flex items-center gap-2 min-w-0">
                <Icon
                  name="mail"
                  className="text-slate-400 text-sm flex-none"
                />
                <dd className="break-all">{previewedContact.email}</dd>
              </div>
            </dl>
            <button
              type="button"
              onClick={() => onConfirm(previewedContact)}
              className="mt-6 flex items-center justify-center gap-2 w-full rounded-lg bg-primary px-3 py-2 text-center text-sm font-semibold text-white shadow-md shadow-black/20 hover:bg-primary/90 transition-all"
            >
              <Icon name="check" className="text-sm" />
              {t("use_contact")}
            </button>
          </div>
        </div>
      ) : (
        <div className="hidden w-96 flex-none items-center justify-center text-sm text-slate-400 sm:flex dark:text-slate-500">
          {t("select_contact")}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create contact (à la volée, envoyé inline à la création de la course)
// ---------------------------------------------------------------------------

function CreateContactForm({
  onCreated,
  onCancel,
  showBackLink = true,
}: {
  onCreated: (data: ContactForm) => void;
  onCancel: () => void;
  showBackLink?: boolean;
}) {
  const t = useTranslations("contact_picker");
  const tCommon = useTranslations("common");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactForm>({ defaultValues: EMPTY_FORM });

  return (
    <form
      onSubmit={(e) => {
        // Cette modale est ouverte depuis un <form> englobant (course-form) :
        // sans stopPropagation, le submit natif de ce formulaire imbriqué
        // remonte (via le arbre React, même à travers le Portal du Dialog)
        // jusqu'au <form> parent et déclenche AUSSI sa propre soumission.
        e.stopPropagation();
        handleSubmit(onCreated)(e);
      }}
      className="p-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-slate-800 dark:text-white">
          {t("new_contact")}
        </h2>
        {showBackLink && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <Icon name="arrow_back" className="text-sm" />
            {t("back_to_list")}
          </button>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          titre={t("first_name")}
          error={errors.prenom?.message}
          {...register("prenom", REQUIRED(tCommon))}
        />

        <Field
          titre={t("last_name")}
          error={errors.nom?.message}
          {...register("nom", REQUIRED(tCommon))}
        />

        <Field
          titre={t("email")}
          type="email"
          containerClassName="sm:col-span-2"
          error={errors.email?.message}
          {...register("email", {
            ...REQUIRED(tCommon),
            pattern: {
              value: EMAIL_REGEX,
              message: t("email_invalid"),
            },
          })}
        />

        <Field
          titre={t("phone")}
          type="tel"
          placeholder="+32470000000"
          containerClassName="sm:col-span-2"
          error={errors.phone?.message}
          {...register("phone", {
            ...REQUIRED(tCommon),
            pattern: {
              value: PHONE_REGEX,
              message: t("phone_invalid"),
            },
          })}
        />
      </div>

      <div className="mt-6 flex justify-end gap-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-white transition-all"
        >
          {tCommon("cancel")}
        </button>
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white shadow-md shadow-black/20 hover:bg-primary/90 transition-all"
        >
          {t("use_contact")}
        </button>
      </div>
    </form>
  );
}
